/**
 * SidePanel Component
 *
 * Simple container for the Assistant view. The header with tabs and actions is
 * managed by ChatSidebar. Opened from the floating command-bar Copilot button;
 * there is no minimized rail.
 */

import React from 'react';
import {
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
    position: 'relative',
    zIndex: 1002, // Above NodeEditorModal (z-index: 1001) to remain visible when editor opens
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
});

/**
 * @deprecated Retained for backwards-compatibility with call sites still
 * threading a tab type through the component tree. `SidePanel` now hosts only
 * the Chat/AI experience; the 'code' variant is unused. Prefer the `isOpen`
 * boolean prop below going forward.
 */
export type SidePanelTab = 'chat';

export interface SidePanelProps {
  /** Whether the panel should render. */
  isOpen: boolean;
  /** Callback when close button is clicked */
  onClose: () => void;
  /** Content to render inside the panel (typically `<ChatSidebar />`). */
  chatContent: React.ReactNode;
}

export const SidePanel: React.FC<SidePanelProps> = ({
  isOpen,
  onClose,
  chatContent,
}) => {
  const styles = useStyles();

  if (!isOpen) {
    return null;
  }

  // Clone chatContent to pass onClose
  const chatContentWithProps = React.isValidElement(chatContent)
    ? React.cloneElement(chatContent as React.ReactElement<{ onClose?: () => void }>, {
        onClose,
      })
    : chatContent;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {chatContentWithProps}
      </div>
    </div>
  );
};

export default SidePanel;
